// The match protocol v1 (docs/platform/netcode.md, crates/protocol/src/{msg,lobby}.rs): message
// shapes and the MessagePack codec. Structs are maps with their field names, data enums are
// internally tagged by "t" (lobby commands by "cmd" inside a {t: "Lobby"} message), byte fields
// are MessagePack bin, unknown keys are ignored.
import { decode, encode } from '@msgpack/msgpack';

export const PROTOCOL_VERSION = 4;
export const TICK_HZ = 60;
export const KEEPALIVE_TICKS = 4;
export const MAX_FRAME_BYTES = 1024;
export const MAX_FRAMES_PER_MSG = 512;
export const PING_INTERVAL_MS = 2000;
export const RESUME_DELAY_STEP_MS = 100;
export const RESUME_DELAY_MAX_MS = 500;
export const MAX_TEAMS = 6;
export const MAX_WORMS = 8;

export type PlayerId = number;
export type Tick = number;

export type Occupant = { t: 'Open' } | { t: 'Human'; player: PlayerId } | { t: 'Ai'; level: number };
/** `speech_bank`: one of `Catalogue.speech_banks` (empty: the server's default bank). `fort`: the
 * team's fort in a fort-mode landscape, an index of `Catalogue.forts` (Scripts/Forts.txt `Fort1` = 0);
 * `fort_extra`: its extras, a `FortExtraType` 0..5 (none, vehicle, sentry gun, magnet, health crate,
 * craft crate: the original lobby's `FortsMan::GetFortExtrasList`). Both 0 when absent. */
export type TeamConfig = { name: string; colour: number; worms: string[]; speech_bank: string; fort?: number; fort_extra?: number };
export type Slot = { occupant: Occupant; team: TeamConfig };
export type Member = { id: PlayerId; name: string; connected: boolean };
/** The random landscape as the original's lobby holds it (options 4..9, docs/port/landgen.md §1.3):
 * a theme, one of its landscape types (`land_type`, an index of `Theme.types`) and a variation of
 * that type (`variation`, an index of its `templates`: the world's template). The server draws the
 * variation when the host picks a theme or a type and on `RerollLandscape`/`RandomiseLandscape`. */
export type Landscape = { theme: string; land_type: number; variation: number; size: number; buildings: boolean; seed: number };
export type MatchSettings = { scheme: string; options: Record<string, string>; worms_per_team: number; landscape: Landscape };
export type RoomPhase = { t: 'Lobby' } | { t: 'Match'; match_no: number };
export type LobbyState = {
  room: string; revision: number; host: PlayerId | null; members: Member[]; slots: Slot[];
  settings: MatchSettings; phase: RoomPhase;
};
/** A landscape type of a theme (Themes.txt `RandomTemplates` `Type`): `title`, a text library id
 * (`FETitle`: `FE.RLG.Islands`, "Single Island"); `templates[i]` is variation `i`'s template. */
export type LandType = { title: string; templates: string[] };
/** `title`: the theme's text library id (`Gui.Theme.Name.Fort`, "Fort Mode"). `types`: its landscape
 * types. `fort`: a fort-mode theme (its templates are fort worlds, `WS_WorldInfo::m_bIsFortMode`). */
export type Theme = { name: string; title: string; types: LandType[]; fort?: boolean };
/** `forts`: the forts' UI names (Scripts/Forts.txt `Name`, `FortsMan` order). */
export type Catalogue = { schemes: string[]; themes: Theme[]; ai_levels: string[]; speech_banks: string[]; forts?: string[] };
export type Owner = { t: 'Human'; player: PlayerId } | { t: 'Ai'; level: number };
/** `wins`: the slot's round wins in the room's earlier matches (NUI_Cache team wins). */
export type MatchTeam = { slot: number; name: string; owner: Owner; wins?: number };

export type LobbyCmd =
  | { cmd: 'SetName'; name: string }
  | { cmd: 'ClaimSlot'; slot: number }
  | { cmd: 'LeaveSlot'; slot: number }
  | { cmd: 'SetTeam'; slot: number; team: TeamConfig }
  | { cmd: 'SetAi'; slot: number; level: number | null }
  | { cmd: 'Kick'; player: PlayerId }
  | { cmd: 'SetHost'; player: PlayerId }
  | { cmd: 'SetSettings'; settings: MatchSettings }
  /** The Landscape Generator's theme: the type of the same title, a drawn variation. */
  | { cmd: 'SetTheme'; theme: string }
  /** The Landscape Generator's type: a drawn variation (a cavern becomes large). */
  | { cmd: 'SetLandType'; land_type: number }
  /** GENERATE: a drawn world seed and variation. */
  | { cmd: 'RerollLandscape' }
  /** The lobby picture's click: a random landscape of the theme (type, variation, seed, size). */
  | { cmd: 'RandomiseLandscape' }
  | { cmd: 'Start' };

export type Resume = { match_no: number; tick: Tick; crc: number; driven: Tick };
export type TickFrame = { tick: Tick; data: Uint8Array };

export type ClientMsg =
  | { t: 'Hello'; protocol: number; name: string; token?: string | null; host_key?: string | null; resume?: Resume | null }
  | ({ t: 'Lobby' } & LobbyCmd)
  | { t: 'Input'; through: Tick; frames: TickFrame[] }
  | { t: 'Crc'; tick: Tick; crc: number }
  | { t: 'Continue' }
  | { t: 'EndMatch' }
  | { t: 'Chat'; text: string }
  | { t: 'Ping'; nonce: number; tick?: Tick | null }
  /** A crafting button press of a team this member plays (CraftingButton record 0x8e). */
  | { t: 'Craft'; team: number; action: number; equip: number }
  | { t: 'Leave' };

export type CatchUpBase = { t: 'Genesis' } | { t: 'Keep'; tick: Tick } | { t: 'Snapshot'; tick: Tick; size: number; crc: number };
export type Takeover = { team: number; tick: Tick };
export type CatchUp = { match_no: number; reason: 'Start' | 'Join' | 'Resync'; base: CatchUpBase; takeover?: Takeover | null };
export type Frames = { through: Tick; released: Tick; frames: TickFrame[] };
export type MatchStart = { match_no: number; setup: Uint8Array; teams: MatchTeam[] };
export type MatchOver = {
  match_no: number; reason: 'Finished' | 'Aborted' | 'Abandoned' | 'Failed' | 'OriginalCrash' | 'Shutdown';
  final_tick?: Tick | null; final_crc?: number | null; ranking?: number[];
  /** With `OriginalCrash`: the crash or hang ("the original game crashes here: …" / "… hangs here: …");
   * with `Shutdown`: the server's reason. */
  detail?: string | null;
};
export type Welcome = {
  protocol: number; room: string; you: PlayerId; token: string;
  server: { sim: string; bundle_sha256?: string | null }; catalogue: Catalogue;
};

export type ServerMsg =
  | ({ t: 'Welcome' } & Welcome)
  | ({ t: 'Lobby' } & LobbyState)
  | ({ t: 'MatchStart' } & MatchStart)
  | ({ t: 'CatchUp' } & CatchUp)
  | { t: 'SnapshotChunk'; offset: number; data: Uint8Array }
  | ({ t: 'Frames' } & Frames)
  | ({ t: 'Takeover' } & Takeover)
  | { t: 'BarrierStatus'; tick: Tick; waiting: PlayerId[] }
  | { t: 'Desync'; tick: Tick; expected?: number | null; got: number; resync: boolean }
  | ({ t: 'MatchOver' } & MatchOver)
  | { t: 'Chat'; from: PlayerId; text: string }
  | { t: 'Pong'; nonce: number }
  /** Another member's crafting press, relayed to this client as the turn's driver: it goes into
   * this client's next driven frame (netcode.md §7). */
  | { t: 'Craft'; team: number; action: number; equip: number }
  | { t: 'Error'; code: string; message: string; fatal: boolean };

export function encodeClient(m: ClientMsg): Uint8Array {
  return encode(m, { ignoreUndefined: true });
}

/** Decodes a server message; byte fields arrive as Uint8Array (MessagePack bin). */
export function decodeServer(b: ArrayBuffer | Uint8Array): ServerMsg {
  const m: unknown = decode(b instanceof Uint8Array ? b : new Uint8Array(b));
  if (!m || typeof m !== 'object' || !('t' in m) || typeof m.t !== 'string') throw new Error('protocol: message without a tag');
  // The server is the protocol's other implementation (crates/protocol); fields are trusted as typed.
  const msg = m as ServerMsg;
  return msg;
}

/** The start of the sim's panic message at a crash of the original game (`sim::core::original_crash::PREFIX`,
 * docs/port/known-original-bugs.md): the match cannot go on, as in the original. */
export const ORIGINAL_CRASH = 'the original game crashes here: ';

/** The same at a state the original never leaves (`sim::core::original_crash::HANG_PREFIX`). */
export const ORIGINAL_HANG = 'the original game hangs here: ';

/** The crash or hang of the original game an error text reports (from `ORIGINAL_CRASH` or
 * `ORIGINAL_HANG` on), if any. */
export function originalCrash(text: string): string | null {
  let i = text.indexOf(ORIGINAL_CRASH);
  if (i < 0) i = text.indexOf(ORIGINAL_HANG);
  return i < 0 ? null : text.slice(i);
}

/** POST /api/matches: a new room. A refusal (429: too many new matches from this address, 503:
 * the server is full or shutting down) throws the server's reason. */
export type NewRoom = { id: string; path: string; ws: string; host_key: string };
export async function createRoom(): Promise<NewRoom> {
  const r = await fetch('/api/matches', { method: 'POST' });
  if (!r.ok) throw new Error(`create match: ${(await r.text().catch(() => '')) || `HTTP ${r.status}`}`);
  return r.json();
}
